/**
 * @file /src/components/demo/Legend.js
 * @description Class that creates rendering of a Legend representing different Finch type colors/labels.
 */
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import settings from '../../utils/settings';

const LEGEND_TYPE_MAPPING = {
    'ACRONYM': { className: 'acronym', label: 'Acronym', background: '#e3cae3', color: '#464D52'},
    'DATETIME': { className: 'datetime', label: 'Datetime', background: '#c9f9d9', color: '#464D52'},
    'GROUP': { className: 'groupType', label: 'Group', background: '#00a2ae', color: '#464D52'},
    'EMAIL': { className: 'email', label: 'Email', background: '#e9d9f9', color: '#464D52'},
    'GEO': { className: 'geo', label: 'Geo/Location', background: '#dff0d1', color: '#464D52'},
    'IPV4': { className: 'ipv4', label: 'IPv4', background: '#7fd0d6', color: '#464D52'},
    'MEASUREMENT': { className: 'measurement', label: 'Measurement', background: '#008A94', color: '#dedede'},
    'MONEY': { className: 'money', label: 'Money', background: '#e9e9e9', color: '#464D52'},
    'PAYMENT': { className: 'payment', label: 'Payment Card', background: '#e9e9e9', color: '#464D52'},
    'NATIONALITY': { className: 'nationality', label: 'Nationality', background: '#a6b3cc', color: '#464D52'},
    'ETHNICITY': { className: 'ethnicity', label: 'Ethnicity', background: '#a6b3cc', color: '#464D52'},
    'LANGUAGE': { className: 'language', label: 'Language', background: '#a6b3cc', color: '#464D52'},
    'ORGANIZATION': { className: 'organization', label: 'Organization', background: '#bfe8eb', color: '#464D52'},
    // 'THING/EVENT': { className: 'event', label: 'THING/EVENT', background: '#D4A190', color: '#464D52'},
    'THING/FACILITY': { className: 'facility', label: 'Facility', background: '#bfe1a4', color: '#464D52'},
    'TICKER': { className: 'ticker', label: 'Stock Ticker', background: '#40b9c2', color: '#464D52'},
    'PERSON': { className: 'person', label: 'Person', background: '#fdeac9', color: '#464D52'},
    'PHONE': { className: 'phone', label: 'Phone Number', background: '#edc7d0', color: '#464D52'},
    'POSITION': { className: 'position', label: 'Position', background: '#fac25e', color: '#464D52'},
    'THING': { className: 'thing', label: 'Thing', background: '#D4A190', color: '#464D52'},
    'TWITTER': { className: 'twitter', label: 'Twitter', background: '#99cc9a', color: '#464D52'},
    'URI': { className: 'uri', label: 'URI', background: '#aa61ad', color: '#464D52'},
    'QUOTE': { className: 'quote', label: 'Quotation', background: '#efe56c', color: '#464D52'},
    'OTHER': { className: 'other', label: 'Other', background: '#e9e9e9', color: '#464D52'}
};

/**
 * @class
 * @description Creates a rendering of a Legend representing different Finch type colors/labels.
 */
class Legend extends Component {

    /** @constructor */
    constructor(props){
        super(props);

        this.state = {
            legendTypes: []
        };

        this.typeMapping = LEGEND_TYPE_MAPPING;
    };

    /**
     * Static utility method that, given a Finch type, returns object with color information.
     * @param {string} type - Finch type
     * @returns Object with color information.
     */
    static getColorDefinition(type) {

        let parts = type.split(/[_\/]/);
        let name = '';
        if (parts.length === 3 && parts[2].toLowerCase() === 'ticker') {
            name = parts[2];
        }
        else {
            name = parts[0];
        }
        let mapping = LEGEND_TYPE_MAPPING[name];
        if (mapping) {
            return mapping;
        }
        return null;
    }

    /**
     * Callback when component is mounted
     */
    componentDidMount() {

        // The rendered legend is based on the types that the user has selected in settings. If the user
        // has not selected any types, we default the legend to showing all types.
    
        let legendTypes = [];
        let types = null;
        if (this.props.types && this.props.types.length) {
            types = this.props.types;            
        } else {
            types = settings.getValue('types',[]);
        }
        if (types && types.length) {
            types.map((type) => {
                if (type.selected && type.name != 'LOCATION') { // LOCATION entity type does NOT go in legend.
                    
                    let parts = type.name.split(/[_\/]/);
                    if (parts.length === 3 && parts[2].toLowerCase() === 'ticker') {
                        if (legendTypes.indexOf(parts[2]) == -1) {
                            legendTypes.push(parts[2]);
                        }
                    }
                    else if (legendTypes.indexOf(parts[0]) == -1) {
                        legendTypes.push(parts[0]);
                    }
                }
            }) 
        } else {
            legendTypes = Object.keys(this.typeMapping).filter((k) => { k != 'QUOTE '});
        }

        this.setState({
            legendTypes
        })
    }

    componentWillReceiveProps(nextProps) { 

        // The rendered legend is based on the types that the user has selected in settings. If the user
        // has not selected any types, we default the legend to showing all types.
    
        let legendTypes = [];
        let types = null;
        types = nextProps.types;   
        if (this.props.types && this.props.types.length) {
            types = this.props.types;            
        } else {
            types = settings.getValue('types',[]);
        }   
        if (types && types.length) {
            types.map((type) => {
                if (type.selected && type.name != 'LOCATION') { // LOCATION entity type does NOT go in legend.
    
                    if (legendTypes.indexOf(type.name) == -1){
                        legendTypes.push(type.name);
                    }
                    let parts = type.name.split(/[_\/]/);
                    if (parts.length === 3 && parts[2].toLowerCase() === 'ticker') {
                        if (legendTypes.indexOf(parts[2]) == -1) {
                            legendTypes.push(parts[2]);
                        }
                    }
                    else if (legendTypes.indexOf(parts[0]) == -1) {
                        legendTypes.push(parts[0]);
                    }
                }
            }) 
        } else {
            legendTypes = Object.keys(this.typeMapping).filter((k) => { k != 'QUOTE '});
        }
        legendTypes.sort()
        legendTypes.push('OTHER')
        this.setState({
            legendTypes
        })
    }

    render() {
        return (
            <div className="legend-wrapper">
                {this.state.legendTypes.map((name) => {
                    let mapping = this.typeMapping[name];
                    if (mapping) {
                        return (<p className="legend" key={`key-${mapping.label}`}>
                            <span className={ `swatch ${mapping.className}` }></span>
                            <span className="text">{ mapping.label }</span>
                        </p>)
                    }
                })}
            </div>
        );
    };
}

Legend.propTypes = {
    types: PropTypes.array
};

export default Legend;


